import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Briefcase, MapPin, Clock, ArrowRight } from 'lucide-react';

const ADMIN_EMAIL = "support@mldcagro.com";

const CareersPage = () => {
  const openPositions = [
    {
      id: 1,
      title: "Operations Manager",
      location: "Greater Noida, UP",
      type: "Full-time",
      department: "Operations"
    },
    {
      id: 2,
      title: "Accounts Manager",
      location: "Greater Noida, UP",
      type: "Full-time",
      department: "Finance"
    },
    {
      id: 3,
      title: "Agricultural Procurement Specialist",
      location: "Anupshahr, UP",
      type: "Full-time",
      department: "Procurement"
    },
    {
      id: 4,
      title: "Warehouse Manager",
      location: "Anupshahr, UP",
      type: "Full-time",
      department: "Logistics"
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#FAFFEE] to-[#EDEDFE] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#D3FF62]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-40 w-96 h-96 bg-[#CACAFC]/30 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[#0C6951] font-semibold uppercase tracking-wider">
              <Link to="/" className="hover:text-[#004534]">Home</Link>
              <ChevronRight className="w-4 h-4" />
              Careers
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#004534] mt-4 mb-6 leading-tight">
              Join Our Team
            </h1>
            <p className="text-xl text-[#0C6951] leading-relaxed">
              Be part of a team that's transforming Indian agriculture and building a financially stronger future for farmers.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6">Why Join MLDC Agro?</h2>
            <p className="text-[#807979] leading-relaxed">
              At MLDC Agro, we believe in creating meaningful impact. Join us in our mission to revolutionize Indian agriculture and make a real difference in farmers' lives.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#FAFAFF] rounded-3xl p-8 text-center">
              <div className="w-16 h-16 bg-[#D3FF62] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Briefcase className="w-8 h-8 text-[#004534]" />
              </div>
              <h3 className="text-xl font-semibold text-[#004534] mb-3">Growth Opportunities</h3>
              <p className="text-[#807979]">Learn and grow with a rapidly expanding agri-tech company</p>
            </div>
            
            <div className="bg-[#FAFAFF] rounded-3xl p-8 text-center">
              <div className="w-16 h-16 bg-[#D3FF62] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-[#004534]" />
              </div>
              <h3 className="text-xl font-semibold text-[#004534] mb-3">Impact-Driven Work</h3>
              <p className="text-[#807979]">Make a real difference in the lives of Indian farmers</p>
            </div>
            
            <div className="bg-[#FAFAFF] rounded-3xl p-8 text-center">
              <div className="w-16 h-16 bg-[#D3FF62] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-[#004534]" />
              </div>
              <h3 className="text-xl font-semibold text-[#004534] mb-3">Collaborative Culture</h3>
              <p className="text-[#807979]">Work with a passionate team in a supportive environment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-[#FAFFEE]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#004534] mb-4">Open Positions</h2>
            <p className="text-[#807979]">Explore current opportunities at MLDC Agro</p>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {openPositions.map((position) => (
              <div
                key={position.id}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between gap-4"
              >
                <div>
                  <h3 className="text-lg font-semibold text-[#004534]">{position.title}</h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#807979]">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {position.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {position.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {position.department}
                    </span>
                  </div>
                </div>
                <a
                  href={`mailto:${ADMIN_EMAIL}?subject=Application for ${position.title}`}
                  className="inline-flex items-center gap-2 bg-[#004534] text-white px-6 py-3 rounded-full font-medium hover:bg-[#0C6951] transition-all duration-200 whitespace-nowrap"
                >
                  Apply Now <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-[#807979] mb-4">Don't see a suitable position?</p>
            <a
              href={`mailto:${ADMIN_EMAIL}?subject=General Application`}
              className="inline-flex items-center gap-2 border-2 border-[#004534] text-[#004534] px-8 py-4 rounded-full font-semibold hover:bg-[#004534] hover:text-white transition-all duration-200"
            >
              Send Your Resume
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
